import React from 'react';
import {
    makeStyles,
    createStyles,
    Theme,
    ThemeProvider,
} from '@material-ui/core/styles';
import {
    Grid,
    Typography,
    Card,
    CardContent,
    Avatar,
    Button,
    Box,
    Container,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText, ButtonTypeMap, ButtonProps,
} from "@material-ui/core";

import Arrow from '../Images/Arrow.png';
import EE from './Images/EE.png';
import CT from './Images/CT.png';
import ESN from './Images/ESN.png';
import HP from './Images/HP.png';
import RI from './Images/RI.png';


import SchoolIcon from '@material-ui/icons/School';
import HomeIcon from '@material-ui/icons/Home';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import FaceIcon from '@material-ui/icons/Face'; // Alternative for FemaleIcon
import PersonIcon from '@material-ui/icons/Person';
import { LandingLayout } from "../index";
import { history } from "../../../configureStore";
import useQueryString from "../../../hooks/useQueryString";
import { getQueryStringValue } from "../../../utils/queryString";
import { useActions } from "../../../actions";
import * as clientActions from "../../../actions/client";
import * as Actions from "../../../actions/verify";
import { useSelector } from "react-redux";
import { RootState } from "../../../reducers"; // Alternative for MaleIcon



// --- Styling (makeStyles for MUI v4) ---
const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            root: {
                paddingTop: theme.spacing(5),
                paddingBottom: theme.spacing(5),
            },
            comparisonCard: {
                textAlign: 'center',
                border: '1px solid #dee2e6',
                borderRadius: 6,
                boxShadow: '0 .125rem .25rem rgba(0,0,0,.075)',
                height: '100%',
                padding: "36px 10px 10px",
            },
            avatar: {
                width: theme.spacing(8),
                height: theme.spacing(8),
                margin: '0 auto',
                marginBottom: theme.spacing(2),
            },
            ctaButton: {
                borderRadius: theme.shape.borderRadius,
                textTransform: 'none',
                fontWeight: 'bold',
                borderColor: '#0dcaf0',
                color: '#0dcaf0',
                padding: theme.spacing(1.5),
                margin: theme.spacing(1),
                whiteSpace: "pre-wrap",
                '&:hover': {
                    backgroundColor: '#0dcaf0',
                    color: '#fff',
                    borderColor: '#0dcaf0',
                },
            },
            benefitAvatar: {
                backgroundColor: '#e7f3ff',
                color: theme.palette.primary.main,
            },
            benefitTitle: {
                fontWeight: 'bold',
            },
            benefitList: {
                padding: 15,
                border: '1px solid #0dcaf0',
                borderRadius: 6,
                textAlign: 'left',
                '& li': {
                    marginBottom: "15px !important",
                },
                '& li div:first-child': {
                    minWidth: 75,
                    minHeight: 75,
                },
                '& .MuiListItemText-primary': {}
            },
            benefitItem: {
                padding: theme.spacing(1, 0),
                borderBottom: '1px solid #0dcaf0',
                '&:last-child': {
                    borderBottom: 'none',
                },
            },
        })
);

// --- Data ---
const comparisonData = [
    {
        name: 'JUDY',
        age: 51,
        gender: 'female' as 'female' | 'male',
        contribution: '$36,842',
        contributionPeriod: 'Contribution over 5 years',
        annualIncome: '$39,000',
        annualIncomePeriod: 'Potential Annual Income 66-86',
        totalDistributions: '$780,000',
        totalDistributionsPeriod: 'Potential Total Income Distributions',
        deathBenefit: '$1,500',
        deathBenefitPeriod: 'Initial Death Benefit',
    },
    {
        name: 'CHRIS',
        age: 21,
        gender: 'male' as 'male' | 'female',
        contribution: '$13,500',
        contributionPeriod: 'Contribution over 5 years',
        annualIncome: '$180,000',
        annualIncomePeriod: 'Potential Annual Income 66-86',
        totalDistributions: '$3,780,000',
        totalDistributionsPeriod: 'Potential Total Income Distributions',
        deathBenefit: '$1,500',
        deathBenefitPeriod: 'Initial Death Benefit',
    },
];

const benefitsData = [
    { img:EE,
        title: 'Education Expenses:', description: 'Supplement tuition, books, or living costs without needing traditional loans' },
    { img:HP,
        title: 'Home Purchase:', description: 'Provide funds for a down payment, helping them enter the housing market sooner' },
    { img:CT,
        title: 'Career Transitions:', description: 'Offer support if they decide to start a business, return to school, or take a new career path' },
    { img:ESN,
        title: 'Emergency Safety Net:', description: 'Be a ready source of fund for unexpected expenses' },
    { img:RI,
        title: 'Retirement Income:', description: 'Later in life, cash value can help supplement retirement savings' },
];

// --- Sub-components ---
interface ComparisonCardProps { data: typeof comparisonData[0]; }
const getInviteLing = (urlToken, clientActions) =>{
     const inviteLink = async (clientPolicyId: string, isIul = false) =>{
        if(isIul){
            // update strategy here
            const isUpdated = await clientActions.updateStrategyId({
                clientPolicyId: clientPolicyId,
                // updating strategyId
                strategyId: 9,
            })
            if(isUpdated){
                // history
                history.push(`/iul/?token=${urlToken}`);
            }
        }else {
            window.scrollTo(0, 0);
            history.push(`/accept/invitation/${urlToken}`);
        }
    }
    return inviteLink
}
const ComparisonCard: React.FC<ComparisonCardProps> = ({ data }) => {
    const classes = useStyles();
    const avatarBgColor = data.gender === 'female' ? '#ff4081' : '#ffc107';

    return (
            <Card className={classes.comparisonCard} style={{border:`2px solid ${data.gender === 'female' ? '#ff4081' : '#ffc107'}` }}>
                <CardContent style={{padding: 0}}>
                    <Grid container spacing={2} justify="center" alignItems="center">
                        <Grid item xs={12} md={6} >
                            <Avatar className={classes.avatar} style={{ backgroundColor: avatarBgColor }}>
                                {data.gender === "female" ? <FaceIcon fontSize="large" /> :
                                        <PersonIcon fontSize="large" />}
                            </Avatar>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" align="left" component="strong">{data.name}, {data.age}</Typography>
                        </Grid>
                    </Grid>
                    <Box mt={2} >
                        {[
                            { value: data.contribution, label: data.contributionPeriod },
                            { value: data.annualIncome, label: data.annualIncomePeriod },
                            { value: data.totalDistributions, label: data.totalDistributionsPeriod },
                            { value: data.deathBenefit, label: data.deathBenefitPeriod },
                        ].map((item, index) => (
                                <Box key={index} mt={1.5}>
                                    <Typography variant="body1" component="strong" align="left" className="mb0 floatLeft w100">{item.value}</Typography>
                                    <Typography variant="caption" align="left" className="mb15 floatLeft w100">{item.label}</Typography>
                                </Box>
                        ))}
                    </Box>
                </CardContent>
            </Card>
    );
};


const CallToActionButton: React.FC<ButtonProps>  = (props) => {
    const classes = useStyles();
    return (
            <Button variant="outlined" className={classes.ctaButton} {...props}>
            </Button>
    );
};

// --- Main Component ---
const KaiZenExperience: React.FC = () => {
    const classes = useStyles();
    const urlToken = getQueryStringValue("token");
    const cActions = useActions(clientActions);
    const inviteLink = getInviteLing(urlToken, cActions)
    const verifyActions = useActions(Actions);
    const inviteToken = getQueryStringValue("token");
    const verifyInviteData = useSelector((state: RootState) => state.verify);
    const clientPolicy = verifyInviteData.client;

    React.useEffect(() => {
        async function vfAccount() {
            await verifyActions.verifyAccount(inviteToken);
        }
        vfAccount();
    }, []);

    return (
            <LandingLayout>
                <Container maxWidth="lg" className={classes.root}>
                    {/* Top Section */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h1" component="h1">
                                <span style={{ color: "#007bff" }}>Fast-track</span> your kid's Kai-Zen experience
                            </Typography>
                            <Typography variant="subtitle1">
                                By purchasing a Kai-Zen policy for your
                                child, you can help jumpstart their financial
                                journey and ensure they don’t get
                                roadblocked by rising costs, savings
                                struggles, or healthcare needs in the future.
                            </Typography>
                            <Typography variant="subtitle1">
                                Starting your child’s policy early (as young
                                as 18) not only locks in premiums based on
                                their current age and health, but also gives
                                their policy ample time to build cash value.
                            </Typography>
                            <Typography variant="subtitle1">
                                On the right, you can see the difference in
                                potential benefits between a customer who
                                purchased her policy at 51 and the policy
                                she purchased for her 21-year-old son.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box position="relative">
                                <Typography variant="h6" color="primary" align="center" gutterBottom className="mb50">
                                    See what starting early can do?
                                </Typography>
                                <img src={Arrow} alt={"Arrow"} style={{
                                    position: "absolute",
                                    top: 0,
                                    right: "15%",
                                    width: 51,
                                    height: "auto",
                                    transform: "translate(-37.55271e-15px, 0px) rotate(156deg)",
                                }}/>
                                <Grid container spacing={2}>
                                    {comparisonData.map((data, index) => (
                                            <Grid item xs={12} sm={6} key={index}>
                                                <ComparisonCard data={data} />
                                            </Grid>
                                    ))}
                                </Grid>
                                <Box textAlign="center" mt={3}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <CallToActionButton onClick={()=>{
                                                inviteLink(clientPolicy.id, false)
                                            }} >
                                                Click here to invite your 18+ child, grandchild, niece, or nephew to
                                                explore Kai-Zen
                                            </CallToActionButton>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <CallToActionButton onClick={()=>{
                                                inviteLink(clientPolicy.id, true)
                                            }} >Is your child under 18? Click here to explore IUL
                                                options for them instead</CallToActionButton>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box my={6}>
                        <Divider />
                    </Box>

                    {/* Bottom Section */}
                    <Grid container spacing={5} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <List className={classes.benefitList}>
                                {benefitsData.map((item, index) => (
                                        <ListItem key={index} disableGutters>
                                            <ListItemIcon>
                                                <img src={item.img} alt={item.title} style={{ width: 60, height: 60,marginTop:10 }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                    primary={<Typography variant="caption" className={classes.benefitTitle}>{item.title}</Typography>}
                                                    secondary={<Typography variant="caption" color="textSecondary">{item.description}</Typography>}
                                            />
                                        </ListItem>
                                ))}
                            </List>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h1" component="h1" gutterBottom>
                                <span style={{ color: "#007bff" }}> Fuel their lives </span> for years to come
                            </Typography>
                            <Typography variant="subtitle1">
                                The cash value inside your child's policy can be borrowed against or withdrawn to help
                                with all kinds of major life milestones, making Kai-Zen a gift that grows with your
                                child.
                            </Typography>
                            <Box textAlign="center" mt={3}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <CallToActionButton onClick={()=>{
                                            inviteLink(clientPolicy.id, false)
                                        }} >Click here to invite your 18+ child, grandchild, niece, or
                                            nephew to explore Kai-Zen</CallToActionButton>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <CallToActionButton onClick={()=>{
                                            inviteLink(clientPolicy.id, true)
                                        }}>Is your child under 18? Click here to explore IUL options
                                            for them instead</CallToActionButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </LandingLayout>
    );
};

export default KaiZenExperience;

